import React from 'react';
import { Box, Checkbox, FormControlLabel, Switch, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';

export type PremiumRequirement = 'instagram_automation' | 'auto_upload';

const CTA_HINT =
    'This activates Instagram automations. Your brief must tell creators to ask their audience to leave a comment to receive a link — and you MUST provide that link for the creator to use in the automated DM.';

export interface PremiumCampaignToggleProps {
    premium: boolean;
    requirements: PremiumRequirement[];
    onPremiumChange: (premium: boolean) => void;
    onRequirementsChange: (requirements: PremiumRequirement[]) => void;
    /**
     * When true (campaign already saved as premium), lock the toggle + requirements
     * so hosts cannot turn premium off or change entitlements after the fact.
     */
    locked?: boolean;
}

function toggleRequirement(
    current: PremiumRequirement[],
    key: PremiumRequirement,
    checked: boolean,
): PremiumRequirement[] {
    if (checked) {
        return current.includes(key) ? current : [...current, key];
    }
    return current.filter(r => r !== key);
}

export function PremiumCampaignToggle({
    premium,
    requirements,
    onPremiumChange,
    onRequirementsChange,
    locked = false,
}: PremiumCampaignToggleProps) {
    const ctaRequired = requirements.includes('instagram_automation');

    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                border: premium ? `1px solid ${BRAND_LUXE_PRIMARY}55` : `1px solid transparent`,
                opacity: locked ? 0.72 : 1,
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 17,
                            color: '#fff',
                            letterSpacing: 0.2,
                        }}>
                        Premium campaign
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.75,
                            fontSize: 14,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255,0.72)',
                            lineHeight: 1.55,
                        }}>
                        Require creators to have a Pro (or higher) plan and OAuth-connected accounts so you can monitor
                        post performance and run Instagram automations.
                    </Typography>
                    {locked ? (
                        <Typography
                            sx={{
                                mt: 1,
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: BRAND_LUXE_PRIMARY,
                                lineHeight: 1.45,
                            }}>
                            Premium is locked on after save — it can’t be turned off for this campaign.
                        </Typography>
                    ) : null}
                </Box>
                <Switch
                    size="small"
                    checked={premium}
                    disabled={locked}
                    onChange={(_, v) => {
                        if (locked) return;
                        onPremiumChange(v);
                        if (v && requirements.length === 0) {
                            onRequirementsChange(['instagram_automation']);
                        }
                        if (!v) onRequirementsChange([]);
                    }}
                    inputProps={{ 'aria-label': 'Premium campaign' }}
                    sx={{
                        flexShrink: 0,
                        mt: 0.25,
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: BRAND_LUXE_SECONDARY,
                            '& + .MuiSwitch-track': { backgroundColor: 'rgba(0,108,80,0.55)', opacity: 1 },
                        },
                        '& .MuiSwitch-track': { opacity: 1, backgroundColor: BRAND_OUTLINE_VARIANT },
                    }}
                />
            </Box>

            {premium ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <FormControlLabel
                        disabled={locked}
                        control={
                            <Checkbox
                                size="small"
                                checked={ctaRequired}
                                disabled={locked}
                                onChange={(_, checked) =>
                                    onRequirementsChange(
                                        toggleRequirement(requirements, 'instagram_automation', checked),
                                    )
                                }
                                sx={{
                                    color: 'rgba(255,255,255,0.45)',
                                    '&.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 14, fontFamily: FONT_ENUM.MAIN_MEDIUM, color: '#fff' }}>
                                CTA / Instagram automation required
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        disabled={locked}
                        control={
                            <Checkbox
                                size="small"
                                checked={requirements.includes('auto_upload')}
                                disabled={locked}
                                onChange={(_, checked) =>
                                    onRequirementsChange(toggleRequirement(requirements, 'auto_upload', checked))
                                }
                                sx={{
                                    color: 'rgba(255,255,255,0.45)',
                                    '&.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 14, fontFamily: FONT_ENUM.MAIN_MEDIUM, color: '#fff' }}>
                                Auto-upload required
                            </Typography>
                        }
                    />
                    {requirements.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: '#f87171', mt: 0.5, lineHeight: 1.45 }}>
                            Select at least one requirement when premium is on.
                        </Typography>
                    ) : null}
                </Box>
            ) : null}

            {premium && ctaRequired ? (
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(255,201,101,0.08)',
                        border: '1px solid rgba(255,201,101,0.28)',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: BRAND_LUXE_PRIMARY,
                            mb: 0.75,
                        }}>
                        Instagram automations
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255,0.82)',
                            lineHeight: 1.55,
                        }}>
                        {CTA_HINT}
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
}
